import { useEffect, useState } from "react";
import { AlertTriangle, FileDown, FileText, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BAJA_TAB } from "@/constants/testIds";
import { toast } from "sonner";

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function BajaTab({ employee, onUpdated }) {
  const employeeId = employee.id;
  const [notices, setNotices] = useState([]);
  const [selectedIncidents, setSelectedIncidents] = useState([]);
  const [noticeDate, setNoticeDate] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState(null);
  const [terminationDate, setTerminationDate] = useState("");
  const [causales, setCausales] = useState([]);
  const [causalFraccion, setCausalFraccion] = useState("");
  const [huboPermiso, setHuboPermiso] = useState(false);
  const [huboJustificacion, setHuboJustificacion] = useState(false);
  const [fechaConocimiento, setFechaConocimiento] = useState("");

  const loadNotices = () => {
    api.get(`/employees/${employeeId}/terminations`).then(({ data }) => setNotices(data));
  };

  useEffect(loadNotices, [employeeId]);
  useEffect(() => {
    api.get("/rescision/causales").then(({ data }) => setCausales(data));
  }, []);

  const toggleIncident = (id) =>
    setSelectedIncidents((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const canGenerate =
    selectedIncidents.length > 0 && !!noticeDate && !!causalFraccion && !!fechaConocimiento;

  const handleGenerate = async () => {
    try {
      const { data } = await api.post(`/employees/${employeeId}/terminations`, {
        notice_date: noticeDate,
        cited_incident_ids: selectedIncidents,
        causal_fraccion: causalFraccion,
        hubo_permiso: huboPermiso,
        hubo_causa_justificada: huboJustificacion,
        fecha_conocimiento_hechos: fechaConocimiento,
      });
      setPreview(data);
      toast.success(data.message);
      loadNotices();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleMarkTerminated = async () => {
    try {
      const { data } = await api.post(`/employees/${employeeId}/mark-terminated`, { termination_date: terminationDate });
      toast.success(data.message);
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDownload = async (format) => {
    try {
      const { data } = await api.get(`/employees/${employeeId}/offboarding-summary`, { params: { format }, responseType: "blob" });
      downloadBlob(data, `resumen_baja_${employee.full_name.replace(/\s+/g, "_")}.${format}`);
    } catch (err) {
      toast.error("No se pudo generar el archivo");
    }
  };

  return (
    <div className="mt-4 space-y-5">
      <div className="bg-card border border-petroleum/10 rounded-2xl p-5">
        <p className="font-heading font-semibold text-charcoal mb-2">Aviso de rescisión asistido (Art. 47 LFT)</p>
        <p className="text-sm text-charcoal/60 mb-3">Responde el cuestionario y selecciona las incidencias documentadas que sustentan la baja. El sistema bloquea la generación si algo no cumple los requisitos legales que puede validar de forma automática.</p>

        <div className="mb-3">
          <Label>Causal (Art. 47 LFT)</Label>
          <select
            data-testid={BAJA_TAB.causalSelect}
            value={causalFraccion}
            onChange={(e) => setCausalFraccion(e.target.value)}
            className="w-full mt-1 border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm"
          >
            <option value="">Selecciona la fracción aplicable...</option>
            {causales.map((c) => (
              <option key={c.fraccion} value={c.fraccion}>Fracción {c.fraccion.replace("_", " ")} — {c.descripcion}</option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <Label>¿Cuándo se enteró la empresa de estos hechos?</Label>
          <Input
            data-testid={BAJA_TAB.fechaConocimientoInput}
            type="date"
            value={fechaConocimiento}
            onChange={(e) => setFechaConocimiento(e.target.value)}
            className="max-w-xs"
          />
          <p className="text-xs text-charcoal/50 mt-1">El derecho a rescindir prescribe en un mes desde esta fecha (Art. 517 fracc. I LFT).</p>
        </div>

        <div className="space-y-2 mb-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid={BAJA_TAB.huboPermisoCheckbox} checked={huboPermiso} onCheckedChange={(v) => setHuboPermiso(!!v)} />
            <span>El trabajador tuvo permiso del patrón</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid={BAJA_TAB.huboJustificacionCheckbox} checked={huboJustificacion} onCheckedChange={(v) => setHuboJustificacion(!!v)} />
            <span>Hubo causa justificada</span>
          </label>
          <p className="text-xs text-charcoal/50">Si marcas cualquiera de las dos, la causal no procede y el sistema bloqueará la generación.</p>
        </div>

        <div className="space-y-2 mb-4">
          {employee.incidents.map((inc) => (
            <label key={inc.id} className="flex items-start gap-3 text-sm cursor-pointer">
              <Checkbox data-testid={`${BAJA_TAB.citeIncidentCheckbox}-${inc.id}`} checked={selectedIncidents.includes(inc.id)} onCheckedChange={() => toggleIncident(inc.id)} className="mt-0.5" />
              <span><span className="text-charcoal/50">{inc.incident_date}</span> — {inc.description}</span>
            </label>
          ))}
          {employee.incidents.length === 0 && <p className="text-sm text-charcoal/50">Sin incidencias registradas — no se puede generar el aviso sin evidencia.</p>}
        </div>

        <div className="mb-3">
          <Label>Fecha del aviso</Label>
          <Input data-testid={BAJA_TAB.noticeDateInput} type="date" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} className="max-w-xs" />
        </div>

        <Button data-testid={BAJA_TAB.generateNoticeButton} disabled={!canGenerate} onClick={handleGenerate} className="bg-terracotta hover:bg-terracotta/90 rounded-full">
          Generar aviso de rescisión
        </Button>

        {preview && (
          <div data-testid={BAJA_TAB.noticePreview} className="mt-4 bg-bone border border-petroleum/10 rounded-xl p-4">
            <p data-testid={BAJA_TAB.draftLegalNotice} className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 mb-3 inline-block">
              {preview.legal_draft_notice}
            </p>
            <p className="text-xs font-semibold text-charcoal/60 mb-1">Causal citada</p>
            <p className="text-sm text-charcoal mb-2">Fracción {preview.causal_fraccion?.replace("_", " ")} — {preview.causal_descripcion}</p>
            <p className="text-xs font-semibold text-charcoal/60 mb-1">Conductas citadas</p>
            <p className="text-sm text-charcoal whitespace-pre-line">{preview.conducts_summary}</p>
            {preview.timing_warning && (
              <div data-testid={BAJA_TAB.timingWarning} className="mt-3">
                <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">Pendiente de validación por abogado laboral</span>
                <p className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {preview.timing_warning_message}
                </p>
              </div>
            )}
            <p className="text-xs text-charcoal/40 mt-2 flex items-center gap-1 font-mono-legal"><Hash className="w-3 h-3" /> #{preview.sequence} · {preview.hash ? `${preview.hash.slice(0, 12)}...` : "sin hash"}</p>
            <p className="text-xs text-red-600/80 mt-2">Pendiente de validación por abogado laboral antes de entregarse.</p>
          </div>
        )}

        {notices.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-semibold text-charcoal/60">Avisos generados anteriormente</p>
            {notices.map((n) => (
              <div key={n.id} data-testid={BAJA_TAB.noticeHistoryRow} className="text-xs text-charcoal/60 bg-accent/30 rounded-lg p-2">
                {n.notice_date} · #{n.sequence} · {n.status === "pending_legal_review" ? "Pendiente de validación legal" : n.status}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card border border-petroleum/10 rounded-2xl p-5">
        <p className="font-heading font-semibold text-charcoal mb-2">Marcar como baja</p>
        {employee.status === "baja" ? (
          <p className="text-sm text-emerald-700">Empleado dado de baja el {employee.termination_date}.</p>
        ) : (
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label>Fecha de baja</Label>
              <Input data-testid={BAJA_TAB.terminationDateInput} type="date" value={terminationDate} onChange={(e) => setTerminationDate(e.target.value)} className="max-w-xs" />
            </div>
            <Button data-testid={BAJA_TAB.confirmTerminatedButton} disabled={!terminationDate} onClick={handleMarkTerminated} className="bg-red-500 hover:bg-red-600 rounded-full">Confirmar baja</Button>
          </div>
        )}
      </div>

      <div className="bg-card border border-petroleum/10 rounded-2xl p-5">
        <p className="font-heading font-semibold text-charcoal mb-2">Resumen para contabilidad/nómina</p>
        <p className="text-sm text-charcoal/60 mb-3">Vacaciones no tomadas por año, historial de salario y políticas/incidencias — sin cálculo de finiquito.</p>
        <div className="flex gap-3">
          <Button data-testid={BAJA_TAB.downloadCsvButton} onClick={() => handleDownload("csv")} variant="outline" className="rounded-full"><FileDown className="w-4 h-4 mr-1" /> Descargar CSV</Button>
          <Button data-testid={BAJA_TAB.downloadPdfButton} onClick={() => handleDownload("pdf")} variant="outline" className="rounded-full"><FileText className="w-4 h-4 mr-1" /> Descargar PDF</Button>
        </div>
      </div>
    </div>
  );
}
